import { baseApi } from '../../../platform/api/base-api'
import type {
  DecisionCommand,
  StoryDecision,
  Timed,
  Receipt,
  ReceiptCommand,
  StoryAction,
} from '../domain/story'
import type {
  RecommendationDecision,
  RecommendationBatch,
  RecommendationCatalog,
  RecommendationTransport,
} from '../domain/recommendation'
import { decode } from './engagement-api'
import { decisionSchema } from './engagement-contracts'
import {
  batchSchema,
  catalogSchema,
  recommendationReceiptSchema,
  unifiedDecisionSchema,
} from './recommendations-contracts'
const path = '/engagement/v3'
const receiptsPath = '/engagement/v2'
const request = (url: string, body: unknown, key?: string) => ({
  url,
  method: 'POST',
  timeout: 5000,
  keepalive: true,
  body,
  ...(key ? { headers: { 'Idempotency-Key': key, 'X-Request-ID': key } } : {}),
})
export const recommendationsApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    unifiedStoryDecision: b.mutation<
      Timed<StoryDecision | RecommendationDecision>,
      DecisionCommand
    >({
      queryFn: async (c, _api, _opt, fetch) => {
        const body = {
          client_id: c.clientId,
          visit_id: c.visitId,
          platform: c.platform,
        }
        const result = await fetch(
          request(`${path}/story-decisions`, body, c.key),
        )
        const code =
          result.error &&
          typeof result.error.data === 'object' &&
          result.error.data !== null &&
          'code' in result.error.data
            ? result.error.data.code
            : undefined
        return result.error &&
          [404, 405].includes(Number(result.error.status)) &&
          (code === undefined ||
            (typeof code === 'string' &&
              ['NOT_FOUND', 'METHOD_NOT_ALLOWED'].includes(code)))
          ? decode(
              await fetch(request('/engagement/story-decisions', body, c.key)),
              decisionSchema,
            )
          : decode(result, unifiedDecisionSchema)
      },
    }),
    recommendationCatalog: b.query<RecommendationCatalog, string>({
      query: () => `${path}/recommendations`,
      transformResponse: (value) => catalogSchema.parse(value),
      providesTags: ['Recommendations'],
    }),
    recommendationManual: b.mutation<
      RecommendationBatch,
      Parameters<RecommendationTransport['manual']>[0]
    >({
      queryFn: async (c, _a, _o, fetch) => {
        const result = decode(
          await fetch(
            request(
              `${path}/recommendation-views`,
              {
                client_id: c.clientId,
                visit_id: c.visitId,
                platform: c.platform,
                recommendation_id: c.recommendationId,
                ...(c.loop ? { loop: true } : {}),
              },
              c.key,
            ),
          ),
          batchSchema,
        )
        return 'data' in result ? { data: result.data.value } : result
      },
    }),
    recommendationActivate: b.mutation<
      RecommendationBatch,
      Parameters<RecommendationTransport['activate']>[0]
    >({
      queryFn: async (c, _a, _o, fetch) => {
        const result = decode(
          await fetch(
            request(
              `${path}/recommendation-batches/${c.batchId}/activate`,
              { client_id: c.clientId },
              c.key,
            ),
          ),
          batchSchema,
        )
        return 'data' in result ? { data: result.data.value } : result
      },
    }),
    recommendationNext: b.mutation<
      RecommendationBatch,
      Parameters<RecommendationTransport['next']>[0]
    >({
      queryFn: async (c, _a, _o, fetch) => {
        const result = decode(
          await fetch(
            request(
              `${path}/recommendation-batches/${c.batchId}/${c.previous ? 'previous' : c.finish ? 'finish' : 'next'}`,
              {
                client_id: c.clientId,
                current_reservation_id: c.reservationId,
                ...(!c.finish && !c.previous ? { reason: c.reason } : {}),
              },
              c.key,
            ),
          ),
          batchSchema,
        )
        return 'data' in result ? { data: result.data.value } : result
      },
    }),
    recommendationImpression: b.mutation<Timed<Receipt>, ReceiptCommand>({
      queryFn: async (c, _a, _o, fetch) =>
        decode(
          await fetch(
            request(`${receiptsPath}/recommendation-impressions`, {
              client_id: c.clientId,
              reservation_id: c.reservationId,
            }),
          ),
          recommendationReceiptSchema,
        ),
      invalidatesTags: (_r, error) => (error ? [] : ['Recommendations']),
    }),
    recommendationInteraction: b.mutation<
      Timed<Receipt>,
      ReceiptCommand & { action: StoryAction }
    >({
      queryFn: async (c, _a, _o, fetch) => {
        if (c.action === 'completed')
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: 'RECOMMENDATION_ACTION_UNSUPPORTED',
            },
          }
        return decode(
          await fetch(
            request(`${receiptsPath}/recommendation-interactions`, {
              client_id: c.clientId,
              reservation_id: c.reservationId,
              action: c.action === 'cta' ? 'details' : 'close',
            }),
          ),
          recommendationReceiptSchema,
        )
      },
      invalidatesTags: (_r, error) => (error ? [] : ['Recommendations']),
    }),
    recommendationSeen: b.mutation<
      void,
      { id: string; sectionId: string; guideRevisionId: string }
    >({
      query: ({ id, sectionId, guideRevisionId }) => ({
        url: `${path}/recommendations/${id}/seen`,
        method: 'PUT',
        body: {
          guide_section_id: sectionId,
          guide_revision_id: guideRevisionId,
        },
        keepalive: true,
        timeout: 5000,
      }),
      invalidatesTags: (_r, error) => (error ? [] : ['Recommendations']),
    }),
  }),
})
